<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <ProgressNavComponent
      v-if="signupStep > 1 && !changingInformationDisabled"
      :steps="navSteps"
      :setStep="setStep"
    />

    <!-- STEP 1: Country selection and onboarding type -->
    <div v-if="shownStep.step_name === LTStepName.CHOOSE_ONBOARDING">
      <CountryPickerComponent />

      <ChooseOnboardingComponent
        :addAuthorizationAgreementFlow="createAuthorizationAccountFlow"
        :addNewDeviceFlow="createNewDeviceFlow"
        :existingCompanies="existingCompanies"
        :countryLocale="'lt'"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
      />
    </div>

    <!-- STEP 2: Company/User Type Selection and Configuration -->
    <div v-if="shownStep.step_name === LTStepName.CONTACT_REVIEW">
      <UserTypeSelectionComponent
        v-if="shouldShowUserTypeSelection"
        :userType="userType"
        :handleUpdateUserType="handleUpdateUserType"
      />

      <MultipleUploadComponent
        v-if="shouldShowPoAUpload"
        :accountData="accountData"
        :hasGenerator="hasGenerator"
        :setLoading="setLoading"
        :uploadItems="POA_UPLOAD_ITEMS"
        :defaultUploadUrl="FILE_UPLOAD_URL"
        @uploadComplete="takeUpload"
        class="mb-4"
      />

      <ContactReviewConfigurationComponent
        v-if="companyIndex !== -1"
        :key="companyIndex"
        :accountData="accountData"
        :companyIndex="companyIndex"
        :userType="userType"
        :shouldShowCompanyFields="userType !== 'private'"
        @handleUpdateCompany="signupStore.updateCompany"
        @updateAccountData="signupStore.updateAccount"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
        class="mb-4"
      />
    </div>

    <!-- DEVICE Step 3: Necessary document upload for registering a new device-->

    <MultipleUploadComponent
      v-if="shownStep.step_name === LTStepName.MULTIPLE_UPLOAD"
      :accountData="accountData"
      :hasGenerator="hasGenerator"
      :setLoading="setLoading"
      :uploadItems="NEW_DEVICE_UPLOAD_ITEMS"
      :defaultUploadUrl="FILE_UPLOAD_URL"
      @uploadComplete="takeUpload"
      class="mb-4"
    />

    <!-- DEVICE Step 4: Generator license select and any extra data associated with new device -->
    <div v-if="shownStep.step_name === LTStepName.GENERATOR_LICENSE">
      <GeneratorLicenseReviewComponent
        v-if="generatorIndex !== -1"
        :accountData="accountData"
        :companyIndex="companyIndex"
        @handleUpdateGenerator="signupStore.updateGenerator"
        class="mb-4"
      />

      <CompanyInfoReviewConfigurationComponent
        v-if="companyIndex !== -1"
        :key="companyIndex"
        :accountData="accountData"
        :companyIndex="companyIndex"
        :existingDataOverride="true"
        :userType="userType"
        @handleUpdateCompany="signupStore.updateCompany"
        @updateAccountData="signupStore.updateAccount"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
        class="mb-4"
      />
    </div>

    <!-- DEVICE Step 5 / AUTH Step 3: Contract review -->
    <ContractReviewConfigurationComponent
      v-if="
        shownStep.step_name === LTStepName.CONTRACT_REVIEW &&
        companyIndex !== -1
      "
      :accountData="accountData"
      :setLoading="setLoading"
      :requiresPoA="requiresPoA"
      @createContainerForStartSigningReady="storeCreateContainerForStartSigning"
      @disableChangingInformation="handleDisableChangingInformation"
      class="mb-4"
    />

    <!-- Buttons -->
    <div
      v-if="signupStep > 1 && !changingInformationDisabled"
      class="d-flex justify-content-between w-100"
    >
      <button
        class="btn btn-secondary me-2"
        :disabled="isLoading"
        @click="setStep(signupStep - 1)"
      >
        {{ $t("signup.navsteps.back") }}
      </button>

      <button
        class="btn btn-primary"
        :disabled="isLoading"
        @click="
          shownStep.step_name === LTStepName.CONTRACT_REVIEW
            ? createContainerForStartSigningFunction()
            : setStep(signupStep + 1)
        "
      >
        {{
          shownStep.step_name === LTStepName.CONTRACT_REVIEW
            ? $t("signup.navsteps.to_signature_page")
            : $t("signup.navsteps.next")
        }}
      </button>
    </div>
    <!-- Loading overlay, shown only when isLoading is true -->
    <LoadingOverlayComponent v-if="isLoading" />
    <!-- AccountDataDebugComponent for debugging -->
    <AccountDataDebugComponent
      :accountData="accountData"
      class="mb-4"
    ></AccountDataDebugComponent>
  </div>
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted } from "vue";
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import ContactReviewConfigurationComponent from "@/signup/lt/ContactReviewConfigurationComponent.vue";
import CompanyInfoReviewConfigurationComponent from "@/signup/lt/CompanyInfoReviewConfigurationComponent.vue";
import ContractReviewConfigurationComponent from "@/signup/lt/ContractReviewConfigurationComponent.vue";
import GeneratorLicenseReviewComponent from "@/signup/lt/GeneratorLicenseReviewComponent.vue";
import ProgressNavComponent from "@/signup/ProgressNavComponent.vue";
import { useSignupStore } from "@/signup/signup";
import { useSignupStepSave } from "@/signup/composables/useSignupStepSave";
import { storeToRefs } from "pinia";
import { useQueryManager } from "@/composables/useQueryManager";
import { useStepFunctions, StepConfig } from "@/composables/useStepFunctions";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import { useI18n } from "vue-i18n";
import MultipleUploadComponent from "@/signup/MultipleUploadComponent.vue";

import ChooseOnboardingComponent from "@/signup/ChooseOnboardingComponent.vue";
import UserTypeSelectionComponent from "@/signup/UserTypeSelectionComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

enum LTStepName {
  CHOOSE_ONBOARDING,
  CONTACT_REVIEW,
  MULTIPLE_UPLOAD,
  GENERATOR_LICENSE,
  CONTRACT_REVIEW,
}

const POA_UPLOAD_ITEMS = [
  {
    fileKey: "lt_power_of_attorney",
    supportedFormats: "common.pdf_file_format",
    required: true,
  },
];

const NEW_DEVICE_UPLOAD_ITEMS = [
  {
    fileKey: "lt_inspection_report",
    required: true,
  },
  {
    fileKey: "lt_property_limit_scheme",
    required: true,
  },
];

const FILE_UPLOAD_URL = "/api/lt/upload-generator-file";

const createContainerForStartSigningFunction = ref<() => void | Promise<void>>(
  () => undefined
);

const isLoading = ref(true);
const { globalErrorHandler } = useGlobalHandlers();
const signupStore = useSignupStore();
const {
  account: accountData,
  companyIndex,
  generatorIndex,
} = storeToRefs(signupStore);
const { updateQuery, getQueryValue, parsers } = useQueryManager();
const signupStep = getQueryValue("step", (value) =>
  value ? parseInt(value, 10) : 1
);
const addAuthorizationAgreement = getQueryValue(
  "addAuthorizationAgreement",
  parsers.boolean
);

const { postStepCallback, takeUpload } = useSignupStepSave(isLoading);
const { t } = useI18n();
const requiresPoA = computed(() => {
  return (
    userType.value === "company" &&
    companyIndex.value !== -1 &&
    !addAuthorizationAgreement.value
  );
});

const userType = getQueryValue<"private" | "company" | undefined>(
  "userType",
  parsers.userType
);

const existingCompanies = computed(() => {
  return accountData.value.companies.filter(
    (company) =>
      company.id !== undefined &&
      company.country === "LT" &&
      company.documents.length > 0
  );
});

const hasGenerator = computed(() => {
  return generatorIndex.value !== -1;
});

const STEP_CONFIG: Record<LTStepName, StepConfig> = {
  [LTStepName.CHOOSE_ONBOARDING]: {
    step_name: LTStepName.CHOOSE_ONBOARDING,
    title: t("signup.navsteps.lt.choose_onboarding"),
    titleKey: "signup.navsteps.lt.choose_onboarding",
  },
  [LTStepName.CONTACT_REVIEW]: {
    step_name: LTStepName.CONTACT_REVIEW,
    title: t("signup.navsteps.lt.contact_review"),
    titleKey: "signup.navsteps.lt.contact_review",
    apiEndpoint: "/api/lt/update-company",
    postStepCallback: postStepCallback,
  },
  [LTStepName.MULTIPLE_UPLOAD]: {
    step_name: LTStepName.MULTIPLE_UPLOAD,
    title: t("signup.navsteps.lt.multiple_upload"),
    titleKey: "signup.navsteps.lt.multiple_upload",
    postStepCallback: postStepCallback,
  },
  [LTStepName.GENERATOR_LICENSE]: {
    step_name: LTStepName.GENERATOR_LICENSE,
    title: t("signup.navsteps.lt.generator_license"),
    titleKey: "signup.navsteps.lt.generator_license",
    apiEndpoint: "/api/lt/update-generator",
    postStepCallback: postStepCallback,
  },
  [LTStepName.CONTRACT_REVIEW]: {
    step_name: LTStepName.CONTRACT_REVIEW,
    title: t("signup.navsteps.lt.contract_review"),
    titleKey: "signup.navsteps.lt.contract_review",
    postStepCallback: postStepCallback,
  },
};

const flowSteps = computed(() => {
  if (addAuthorizationAgreement.value) {
    return [
      STEP_CONFIG[LTStepName.CHOOSE_ONBOARDING],
      STEP_CONFIG[LTStepName.CONTACT_REVIEW],
      STEP_CONFIG[LTStepName.CONTRACT_REVIEW],
    ];
  } else {
    return [
      STEP_CONFIG[LTStepName.CHOOSE_ONBOARDING],
      STEP_CONFIG[LTStepName.CONTACT_REVIEW],
      STEP_CONFIG[LTStepName.MULTIPLE_UPLOAD],
      STEP_CONFIG[LTStepName.GENERATOR_LICENSE],
      STEP_CONFIG[LTStepName.CONTRACT_REVIEW],
    ];
  }
});

const { shownStep, setStep, navSteps } = useStepFunctions(
  flowSteps,
  signupStep
);

const setLoading = (value: boolean) => {
  isLoading.value = value;
};

const shouldShowUserTypeSelection = computed(() => {
  // Only show if no existing company is selected that has finished registration
  if (companyIndex.value === -1) {
    return true;
  } else {
    return !accountData.value.companies[companyIndex.value].has_registered;
  }
});

const shouldShowPoAUpload = computed(() => {
  return (
    !addAuthorizationAgreement.value &&
    userType.value === "company" &&
    companyIndex.value === -1
  );
});

const storeCreateContainerForStartSigning = (
  createContainerForStartSigning: () => Promise<void>
) => {
  createContainerForStartSigningFunction.value = createContainerForStartSigning;
};

const changingInformationDisabled = ref(false);
const handleDisableChangingInformation = () => {
  changingInformationDisabled.value = true;
};

const handleUpdateUserType = async (
  type: "private" | "company" | undefined
) => {
  await updateQuery({ userType: type });
};

const createAuthorizationAccountFlow = async () => {
  await updateQuery({ addAuthorizationAgreement: "true", step: "2" });
};

const createNewDeviceFlow = async () => {
  await updateQuery({ addAuthorizationAgreement: "false", step: "2" });
};

const handleSelectExistingCompany = async (companyId: number) => {
  isLoading.value = true;

  const company = accountData.value.companies.find((c) => c.id === companyId);

  // Lithuanian personal ID numbers are 11 digits long
  const newUserType =
    company?.registry_code?.length === 11 ? "private" : undefined;

  try {
    await updateQuery({
      company_id: companyId.toString(),
      userType: newUserType,
      step: "2",
    });
    signupStore.removeEmptyCompanyIfExists();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const handleResetCompanySelection = () => {
  updateQuery({
    company_id: undefined,
    userType: undefined,
    generator_id: undefined,
    step: "1",
  });
  signupStore.removeEmptyCompanyIfExists();
};

onMounted(async () => {
  // Set userType based on company registry code if we have a selected company
  if (
    userType.value === undefined &&
    companyIndex.value !== -1 &&
    accountData.value.companies[companyIndex.value]
  ) {
    const registryCode =
      accountData.value.companies[companyIndex.value].registry_code;

    // Note that this wait is necessary to ensure that the registry_code is set,
    // because query updates seem to be fragile at mount

    await new Promise((r) => setTimeout(r, 200));
    if (registryCode) {
      if (registryCode.length === 11) {
        await handleUpdateUserType("private");
      } else if (registryCode.length === 9) {
        await handleUpdateUserType("company");
      } else {
        await handleUpdateUserType(undefined);
      }
    }
  }
  isLoading.value = false;
});

// Watch user type selection and add a new company if needed
watch(
  userType,
  (newValue) => {
    if (
      newValue === "private" ||
      (newValue === "company" && addAuthorizationAgreement.value)
    ) {
      signupStore.createCompanyIfMissing();
    } else {
      signupStore.removeEmptyCompanyIfExists();
    }
  },
  { immediate: true }
);
</script>
